import React, { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Select from "../common/Select";
import Alert from "../common/Alert";
import {
  useApplyPosOrderDiscountMutation,
  useRemovePosOrderDiscountMutation,
} from "../../store/api";
import { getPosErrorMessage } from "../../utils/posErrorHandler";

/**
 * DiscountForm Component
 * Modal form for applying discounts to orders
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close callback
 * @param {string} props.orderId - Order ID
 * @param {number} props.orderSubtotal - Order subtotal for validation
 */
const initialForm = {
  discountType: "MANUAL",
  amount: "",
  code: "",
};

const DiscountForm = ({
  isOpen,
  onClose,
  orderId,
  orderSubtotal = 0,
  discounts = [],
}) => {
  const [applyDiscount, { isLoading, error }] =
    useApplyPosOrderDiscountMutation();
  const [removeDiscount, { isLoading: isRemoving, error: removeError }] =
    useRemovePosOrderDiscountMutation();

  const [form, setForm] = useState(initialForm);
  const existingDiscount = useMemo(
    () => (Array.isArray(discounts) && discounts.length > 0 ? discounts[0] : null),
    [discounts],
  );

  useEffect(() => {
    if (!isOpen) return;

    if (existingDiscount) {
      setForm({
        discountType: existingDiscount.discountType || "MANUAL",
        amount:
          existingDiscount.amount === null || existingDiscount.amount === undefined
            ? ""
            : String(existingDiscount.amount),
        code: existingDiscount.code || "",
      });
      return;
    }

    setForm(initialForm);
  }, [existingDiscount, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(form.amount);

    // Validation
    if (isNaN(amount) || amount <= 0) {
      return;
    }

    if (amount > orderSubtotal) {
      return;
    }

    try {
      if (existingDiscount?.discountId) {
        await removeDiscount({
          orderId,
          discountId: existingDiscount.discountId,
        }).unwrap();
      }

      await applyDiscount({
        orderId,
        payload: {
          discountType: form.discountType,
          amount,
          code: form.code || undefined,
        },
      }).unwrap();

      setForm(initialForm);
      onClose();
    } catch (err) {
      // Error handled by error state
    }
  };

  const handleRemove = async () => {
    if (!existingDiscount?.discountId) return;

    try {
      await removeDiscount({
        orderId,
        discountId: existingDiscount.discountId,
      }).unwrap();
      setForm(initialForm);
      onClose();
    } catch (err) {
      // Error handled by error state
    }
  };

  const handleCancel = () => {
    setForm(initialForm);
    onClose();
  };

  const amount = parseFloat(form.amount) || 0;
  const isValid = amount > 0 && amount <= orderSubtotal;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={existingDiscount ? "Edit Discount" : "Apply Discount"}
      confirmText={existingDiscount ? "Update" : "Apply"}
      onConfirm={handleSubmit}
      confirmDisabled={!isValid || isLoading || isRemoving}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {existingDiscount && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-emerald-700 dark:text-emerald-300">
                Current discount:{" "}
                <span className="font-semibold">
                  {existingDiscount.code || existingDiscount.discountType}
                </span>{" "}
                (-${Number(existingDiscount.amount || 0).toFixed(2)})
              </div>
              <button
                type="button"
                onClick={handleRemove}
                disabled={isRemoving || isLoading}
                className="rounded border border-emerald-300 px-2 py-1 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
              >
                {isRemoving ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        )}

        {/* Discount Type */}
        <Select
          label="Discount Type"
          name="discountType"
          value={form.discountType}
          onChange={handleChange}
          required
        >
          <option value="MANUAL">Manual Discount</option>
          <option value="PERCENTAGE">Percentage</option>
          <option value="FIXED">Fixed Amount</option>
        </Select>

        {/* Amount */}
        <Input
          label="Discount Amount"
          name="amount"
          type="number"
          step="0.01"
          min="0.01"
          max={orderSubtotal}
          placeholder="0.00"
          value={form.amount}
          onChange={handleChange}
          required
          helper={`Maximum: $${orderSubtotal.toFixed(2)}`}
        />

        {/* Code (Optional) */}
        <Input
          label="Discount Code (Optional)"
          name="code"
          placeholder="STAFF, PROMO, etc."
          value={form.code}
          onChange={handleChange}
          helper="For reference and reporting"
        />

        {/* Preview */}
        {amount > 0 && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-emerald-700 dark:text-emerald-300">
                Discount Amount:
              </span>
              <span className="font-bold text-emerald-700 dark:text-emerald-300">
                -${amount.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-emerald-700 dark:text-emerald-300">
                New Subtotal:
              </span>
              <span className="font-bold text-emerald-700 dark:text-emerald-300">
                ${(orderSubtotal - amount).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Validation Error */}
        {amount > orderSubtotal && (
          <Alert tone="warning">
            Discount amount cannot exceed order subtotal.
          </Alert>
        )}

        {/* API Error */}
        {error && <Alert tone="error">{getPosErrorMessage(error)}</Alert>}
        {removeError && (
          <Alert tone="error">{getPosErrorMessage(removeError)}</Alert>
        )}
      </form>
    </Modal>
  );
};

export default DiscountForm;
